import { Card } from '@amzn/stencil-react-components/card';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { H3, H4 } from '@amzn/stencil-react-components/text';

import { InfoTooltip } from '../../../shared/ui/forms/InfoTooltip';
import { VerticalDivider } from '../../../shared/ui/VerticalDivider';
import { DocLinksField } from '../../../shared/ui/forms/DocLinksField';
import { useMessages } from '../../../shared/i18n/useMessages';
import {
  CONFIG_BUNDLE,
  type ConfigKey,
  dtProps,
  type QuestionConfigCardProps,
} from './questionConfigShared';

/**
 * The "Documentation" card of the Question Configuration step — the supporting
 * SOP / reference links that back the audit question. Wraps the shared
 * `DocLinksField` (repeatable title + URL rows with add/remove and URL
 * validation). Fully controlled via `patchConfig`.
 */
export function QuestionConfigDocumentation({
  config,
  patchConfig,
  disabled,
  dataTestId,
}: QuestionConfigCardProps): JSX.Element {
  const { t } = useMessages<ConfigKey>(CONFIG_BUNDLE);
  const infoAltText = t('configInfoAltText');

  return (
    <Card padding="dimensions.spacing.300" {...dtProps(dataTestId, 'doc-section')}>
      <Col gridGap="dimensions.spacing.300" width="100%">
        {/* Section title. */}
        <H3>{t('configDocumentationHeading')}</H3>

        {/* SOP document-links block: its own "Standard Operating Procedures"
         *  sub-heading + Info tooltip, then the links table — mirrors the
         *  manual workflow's Documentation step. */}
        <Row alignItems="center" gridGap="dimensions.spacing.200">
          <H4>{t('configDocSopHeading')}</H4>
          <VerticalDivider />
          <InfoTooltip
            text={t('configDocumentationInfo')}
            ariaLabel={infoAltText}
            {...dtProps(dataTestId, 'doc-info')}
          />
        </Row>
        <DocLinksField
          value={config.docLinks}
          onChange={(next) => patchConfig({ docLinks: next })}
          disabled={disabled}
          labels={{
            documentColumnHeader: t('configDocDocumentColumn'),
            linkColumnHeader: t('configDocLinkColumn'),
            titlePlaceholder: t('configDocTitlePlaceholder'),
            titleAriaLabel: t('configDocTitleAriaLabel'),
            urlPlaceholder: t('configDocUrlPlaceholder'),
            urlAriaLabel: t('configDocUrlAriaLabel'),
            addButton: t('configDocAddButton'),
            removeAltText: t('configDocRemoveAltText'),
            emptyText: t('configDocEmptyText'),
            invalidUrlText: t('configDocInvalidUrl'),
            duplicateUrlText: t('configDocDuplicateUrl'),
          }}
          {...dtProps(dataTestId, 'doc-links')}
        />
      </Col>
    </Card>
  );
}
